import {
	DirectionsCar as DirectionsCarIcon,
	LocationOn as LocationOnIcon,
	Logout as LogoutIcon,
	MyLocation as MyLocationIcon,
	Person as PersonIcon,
	StopCircle as StopCircleIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, ButtonOwnProps, Typography } from "@mui/material";
import moment from "moment";
import { FC, useState } from "react";

import { IActiveSessionDetails } from ".";
import { AGDialog } from "../../../components/AGDialog";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import { useNotification } from "../../../components/FeedbackNotification/useNotification";
import { useForceLogoutUserMutation } from "../../../redux/reducers/userManagement.reducer";

interface ISessionDetailsDialogProps {
	open: boolean;
	sessionDetails: IActiveSessionDetails | null;
	onClose: () => void;
}

interface IConfirmationDialogDetails {
	open: boolean;
	title: string;
	heading: string;
	color: ButtonOwnProps["color"];
	confirmationButtonText: string;
}

const SessionDetailsDialog: FC<ISessionDetailsDialogProps> = (props) => {
	const { open, sessionDetails, onClose } = props;

	const { notify } = useNotification();

	const [confirmationDialogDetails, setConfirmationDialogDetails] = useState<IConfirmationDialogDetails>({
		open: false,
		title: "",
		heading: "",
		color: undefined,
		confirmationButtonText: ""
	});

	// APIS
	// FORCE LOGOUT USER
	const [forceLogoutUser, { isLoading: forceLogoutUserLoading }] = useForceLogoutUserMutation();

	function handleOpenConfirmationDialog(type: "enforcement" | "session") {
		if (type === "enforcement") {
			setConfirmationDialogDetails({
				open: true,
				title: "Force Stop Enforcement",
				heading: "Are you sure you want to forcefully stop the ongoing enforcement?",
				color: "warning",
				confirmationButtonText: "Stop Enforcement"
			});
		} else if (type === "session") {
			setConfirmationDialogDetails({
				open: true,
				title: "Force Logout User",
				heading: "Are you sure you want to forcefully logout this user?",
				color: "error",
				confirmationButtonText: "Logout User"
			});
		}
	}

	function handleCloseConfirmationDialog() {
		setConfirmationDialogDetails((prevDetails) => ({
			...prevDetails,
			open: false
		}));
	}

	function handleSubmitConfirmationDialog() {
		if (sessionDetails) {
			handleCloseConfirmationDialog();
			forceLogoutUser(sessionDetails.id)
				.unwrap()
				.then(() => {
					notify("User logged out successfully", "success");
					onClose();
				})
				.catch((error) => {
					notify("data" in error && error.data ? String(error.data) : "Something went wrong", "error");
				});
		}
		// onClose();
	}

	return (
		<AGDialog maxWidth="sm" color="primary" title="Active Session" open={open} onClose={onClose}>
			{!sessionDetails ? (
				<Typography variant="h5" color="textSecondary" textAlign="center" fontWeight={500}>
					Session not found!
				</Typography>
			) : (
				<Box className="session-details-dialog-content">
					<Box className="session-user-details-wrapper">
						<Box className="session-user-icon">
							<PersonIcon sx={{ fontSize: "42px" }} />
						</Box>

						<Box className="session-user-details">
							<Typography fontWeight={500} fontSize="18px">
								{sessionDetails.user_name}
							</Typography>
							<Typography variant="body2" color="success" fontSize="16px">
								Active since{" "}
								<span style={{ fontWeight: 500 }}>
									{moment(sessionDetails.active_since).format("DD/MM/YYYY hh:mm A")}
								</span>
							</Typography>
						</Box>
					</Box>

					{sessionDetails.enforcement ? (
						<Box className="session-enforcement-wrapper">
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<Box>
									<Typography color="primary" fontWeight={500} fontSize="18px">
										Enforcement Session #{sessionDetails.enforcement.id}
									</Typography>

									<Typography variant="body2" color="info">
										started at {moment(sessionDetails.enforcement.start_time).format("DD/MM/YYYY hh:mm A")}
									</Typography>
								</Box>

								<Button
									disableElevation
									variant="contained"
									color="warning"
									startIcon={<StopCircleIcon />}
									size="small"
									sx={{ textTransform: "none" }}
									onClick={() => handleOpenConfirmationDialog("enforcement")}
								>
									Stop Enforcement
								</Button>
							</Box>

							<Box className="session-enforcement-grid">
								<Box className="session-enforcement-details">
									<PersonIcon color="action" fontSize="inherit" />

									<Box>
										<Typography variant="body2">Officer Name</Typography>
										<Typography fontWeight={500}>{sessionDetails.enforcement.officer_name}</Typography>
									</Box>
								</Box>

								<Box className="session-enforcement-details">
									<DirectionsCarIcon color="action" fontSize="inherit" />

									<Box>
										<Typography variant="body2">Interceptor ID</Typography>
										<Typography fontWeight={500}>{sessionDetails.enforcement.interceptor_id}</Typography>
									</Box>
								</Box>

								<Box className="session-enforcement-details">
									<LocationOnIcon color="action" fontSize="inherit" />

									<Box>
										<Typography variant="body2">District</Typography>
										<Typography fontWeight={500}>{sessionDetails.enforcement.location_name}</Typography>
									</Box>
								</Box>

								<Box className="session-enforcement-details">
									<MyLocationIcon color="action" fontSize="inherit" />

									<Box>
										<Typography variant="body2">Coordinates</Typography>
										<Typography fontWeight={500}>
											{`${sessionDetails.enforcement.latitude}, ${sessionDetails.enforcement.longitude}`}
										</Typography>
									</Box>
								</Box>
							</Box>
						</Box>
					) : null}

					<Box className="session-details-dialog-footer">
						<LoadingButton
							disableElevation
							variant="contained"
							color="error"
							startIcon={<LogoutIcon />}
							onClick={() => handleOpenConfirmationDialog("session")}
							loading={forceLogoutUserLoading}
							loadingPosition="start"
						>
							Force Logout User
						</LoadingButton>
					</Box>
				</Box>
			)}

			<ConfirmationDialog
				open={confirmationDialogDetails.open}
				title={confirmationDialogDetails.title}
				heading={confirmationDialogDetails.heading}
				color={confirmationDialogDetails.color}
				submitButtonText={confirmationDialogDetails.confirmationButtonText}
				width="xs"
				warning="This action is not reversible"
				warningColor="var(--color-error-main)"
				onClose={handleCloseConfirmationDialog}
				onConfirm={handleSubmitConfirmationDialog}
			/>
		</AGDialog>
	);
};

export default SessionDetailsDialog;
